import "./Yearly.css"
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import APIURL from "../../common/Config";
import Header from "../header/header";
import Footer from "../footer/footer";

const Taurusyearly = () => {
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inMemoryCacheRef = useRef(new Map());
  const [activeIndex, setActiveIndex] = useState(null);

  // Static metadata for TAURUS
  const staticMeta = {
    title: "AstroBhagya – Free Taurus Yearly Horoscope by Best Astrologer",
    description: "Looking for accurate Taurus yearly horoscope predictions 2026? Discover free Taurus astrology predictions and annual insights by zodiac sign for a better year ahead.",
    keywords: "Free Taurus yearly horoscope predictions 2026, Taurus yearly Astrology predictions, Taurus annual predictions, Yearly horoscope by Taurus zodiac sign",
    author: "Heavenly Expectation Private Limited",
  };

  const toggleAnswer = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const fetchTaurusYearly = useCallback(async (retries = 3) => {
    const dateStr = "20260101"; // 2026 data
    const cacheKey = `taurus_yearly_${dateStr}`; 
    setError(null);
    setLoading(true);

    // 1. CHECK LOCALSTORAGE
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        setHoroscopeData(parsed);
        setLoading(false);
        return;
      } catch (e) {
        console.error("Failed to parse cached data:", e);
      }
    }

    // 2. CHECK IN-MEMORY CACHE
    if (inMemoryCacheRef.current.has(cacheKey)) {
      setHoroscopeData(inMemoryCacheRef.current.get(cacheKey));
      setLoading(false);
      return;
    }

    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500));

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.warn('Fetch request timed out after 15 seconds.');
      }, 15000);

      const response = await fetch(
        `${APIURL}/horroscope/getallprediction?type=yearly&startDate=${dateStr}&endDate=${dateStr}&language=english`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        // Find TAURUS data (id: 30 or sunShine contains "TAURUS") 
        const taurusData = data.find(
          item => item.id === 30 || item.sunShine?.toUpperCase().includes("TAURUS")
        );

        if (taurusData) {
          // Cache the data
          inMemoryCacheRef.current.set(cacheKey, taurusData);
          localStorage.setItem(cacheKey, JSON.stringify(taurusData));
          setHoroscopeData(taurusData);
        } else {
          throw new Error("Taurus data not found in API response");
        }
      } else {
        throw new Error("Unexpected response format from API");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('The network request timed out. Please check your internet connection and try again.');
        console.error("Network timeout error:", error);
      } else {
        setError(`Failed to load yearly horoscope: ${error.message}. Please try again.`);
        console.error("Error fetching Taurus yearly data:", error);
      }

      if (retries > 0) {
        console.log(`Retrying fetch for Taurus Yearly. Retries left: ${retries - 1}`);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return fetchTaurusYearly(retries - 1);
      } else {
        // FALLBACK DATA - TAURUS 2026 
        console.log("All retries exhausted. Using fallback data.");
        const fallbackData = {
          sunShine: "Taurus 2026: Your year, as the stars align",
          prediction: `<div class="horoscope">
            <p>As 2026 begins, Taurus, the year invites you to rethink how you define comfort, success, and personal satisfaction. Unlike years that focus on stability alone, 2026 pushes you to question routines that no longer serve your growth.</p>
            <h2>Love and Relationships</h2>
            <p>In 2026, Taurus, relationships ask for honesty and clarity rather than emotional comfort alone. If you are in a relationship, this year may bring important conversations about expectations and future direction.</p>
            <h2>Career and Finances</h2>
            <p>Career matters in 2026 revolve around responsibility and decision-making. Taurus professionals may face moments where they must choose between comfort and growth.</p>
            <h2>Health & Wellness</h2>
            <p>Health in 2026 requires conscious attention, Taurus. Stress-related problems may occur if routines become irregular. Maintaining consistent sleep and regular meals will be beneficial.</p>
            <h2>Astrological Remedies</h2>
            <ul>
              <li><strong>Wear White Sapphire or Opal:</strong> These gemstones may help bring emotional clarity and balanced decision-making.</li>
              <li><strong>Friday Simplicity Ritual:</strong> Keeping surroundings clean and wearing light colours on Fridays can strengthen Venus energies.</li>
              <li><strong>Charity for Stability:</strong> Donating food or daily-use items can help reduce mental stress.</li>
            </ul>
          </div>`
        };
        setHoroscopeData(fallbackData);
      }
    } finally {
      await minLoadingTime;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTaurusYearly();
  }, [fetchTaurusYearly]);

  // Set meta tags
  useEffect(() => {
    document.title = staticMeta.title;

    const setOrCreateMetaTag = (name, content) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (metaTag) {
        metaTag.setAttribute("content", content);
      } else {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("name", name);
        metaTag.setAttribute("content", content);
        document.head.appendChild(metaTag);
      }
    };

    setOrCreateMetaTag("description", staticMeta.description);
    setOrCreateMetaTag("keywords", staticMeta.keywords);
    setOrCreateMetaTag("author", staticMeta.author);
    setOrCreateMetaTag("revisit-after", "7 days");
    setOrCreateMetaTag("distribution", "global");
    setOrCreateMetaTag("rating", "general");

    let charsetMeta = document.querySelector("meta[charset]");
    if (!charsetMeta) {
      charsetMeta = document.createElement("meta");
      charsetMeta.setAttribute("charset", "UTF-8");
      document.head.prepend(charsetMeta);
    }
  }, []);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner" />
        <p>Loading Taurus 2026 Horoscope...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button className="retry-button" onClick={() => fetchTaurusYearly()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div>
        <section id="Make">
          <div className="year-container">
            <h4 className="mt-3 mb-3 text-center font-weight-bold">
              {horoscopeData?.sunShine || "Taurus 2026: Your year, as the stars align"}
            </h4>
            <div className="icons-wrapper text-center mb-4">
              <img
                src={require("../../assets/Taurusss.webp")}
                alt="Taurus"
                className="horoscope-icon mx-2"
              />
            </div>

            {horoscopeData?.prediction && (
              <main>
                <div
                  dangerouslySetInnerHTML={{
                    __html: horoscopeData.prediction
                  }}
                />
              </main>
            )}

            <div className="btn-wrapper">
              <Link to="/yearly-horoscope/aries" className="btn btn-customs">
                <i className="bi bi-arrow-left"></i> Aries Horoscope 2026
              </Link>
              <Link to="/yearly-horoscope/gemini" className="btn btn-customs">
                <i className="bi bi-arrow-right"></i> Gemini Horoscope 2026
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ SECTION - SAME AS ARIES */}
        <div className="faq-section">
          <div className="container-item">
            <h3>Frequently Asked Questions (FAQs) – Taurus Yearly Horoscope</h3>
            <div className="fitem">
              {[
                {
                  question: "1. What are the core personality traits of Taurus?",
                  answer: "Taurus individuals are known for their reliability, patience, and practical approach to life. Ruled by Venus, they value stability, comfort, and long-term security.",
                },
                {
                  question: "2. What can Taurus expect in love and relationships in 2026?",
                  answer: "In 2026, Taurus relationships focus on honesty and emotional clarity. Expect deeper conversations about expectations and future plans with partners.",
                },
                {
                  question: "3. What does 2026 look like for Taurus career growth?",
                  answer: "Career in 2026 requires choosing between comfort and growth. Opportunities in administration, finance, and real estate will reward patient professionals.",
                },
                {
                  question: "4. What health advice for Taurus in 2026?",
                  answer: "Focus on lifestyle correction with consistent sleep, meals, and light exercise. Stress management and nature connection improve well-being.",
                },
                {
                  question: "5. Key astrological remedies for Taurus?",
                  answer: "Wear White Sapphire/Opal, Friday rituals, and charity donations help balance Venus energies and reduce mental stress.",
                },
              ].map((item, index) => (
                <div className="faq-item" key={index}>
                  <div
                    className={`faq-question ${
                      activeIndex === index ? "active" : ""
                    }`}
                    onClick={() => toggleAnswer(index)}
                  >
                    {item.question}
                  </div>
                  <div
                    className={`faq-answer ${
                      activeIndex === index ? "show" : ""
                    }`}
                  >
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Taurusyearly; 
